import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid } from '@web/utils';
import { Observable } from 'rxjs';
import { DictionaryDetailsForm } from '../../../shared/models/dictionary-details-form';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { DictionaryDetailsFormComponent } from '../../../shared/containers/dictionary-details-form/dictionary-details-form.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpErrorService } from '@web/common';
import { AdminDictionaryProductAttributeDataSourceService } from '../../services/admin-dictionary-product-attribute-data-source.service';

@Component({
  selector: 'app-dictionary-product-attribute-edit',
  imports: [
    CommonModule,
    SpinnerFilledComponent,
    DictionaryDetailsFormComponent,
    AlertsComponent,
  ],
  templateUrl: './admin-dictionary-product-attribute-edit.component.html',
  styleUrl: './admin-dictionary-product-attribute-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminDictionaryProductAttributeEditComponent {
  /* prettier-ignore */
  private readonly dictionaryDataSource = inject(AdminDictionaryProductAttributeDataSourceService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public readonly id = input.required<Guid>();
  public readonly submitSuccessEvent = output();

  protected data$!: Observable<DictionaryDetailsForm>;

  private readonly destroyRef = inject(DestroyRef);

  public constructor() {
    effect(() => this.configureDataSource());
  }

  public handleSubmitClickEvent(data: DictionaryDetailsForm): void {
    this.dictionaryDataSource
      .updateByFormData(this.id(), data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.submitSuccessEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private configureDataSource(): void {
    this.data$ = this.dictionaryDataSource.fetchDetailsFormById(this.id());
    this.changeDetectorRef.detectChanges();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }
}
